import {
  NacelleConnector,
  NacelleConnectorParams
  // FetchProductParams,
  // FetchProductsParams,
  // FetchCollectionParams,
  // FetchCollectionPageParams,
  // FetchContentParams,
  // FetchPageParams,
  // FetchPagesParams,
  // FetchBlogParams,
  // FetchArticleParams,
  // FetchArticlesParams,
  // FetchBlogPageParams
} from '../nacelleConnector'
import HttpClientAxios from '../../util/http/HttpClientAxios'
import { GraphQLResponseRoot, GraphQLPageResponse } from './utils/interfaces'
import { Product, ProductOptions, createProduct } from '../../objects/product'
import {
  Collection,
  CreateCollectionOptions,
  createCollection
} from '../../objects/collection'
import {
  Content,
  CreateContentOptions,
  createContent
} from '../../objects/content'
import { Space, CreateSpaceOptions, createSpace } from '../../objects/space'
import { CartItem, Checkout, CheckoutMetafield } from '../../objects/checkout'
import getProductsQuery from './queries/getProductsQuery'
import getCollectionsQuery from './queries/getCollectionsQuery'
import getContentQuery from './queries/getContentQuery'
import getSpaceQuery from './queries/getSpaceQuery'
import getCheckoutQuery from './queries/getCheckoutQuery'
import getVariantAvailabilityQuery from './queries/getVariantAvailabilityQuery'
import processCheckoutMutation from './mutations/processCheckoutMutation'

export interface NacelleGraphQLConnectorParams extends NacelleConnectorParams {
  endpoint: string
  spaceId: string
  token: string
}

export interface NacelleGraphQLRequestResponse<T> {
  success: boolean
  data: T | null
  error?: string
}

export interface ProcessCheckoutParams {
  cartItems: CartItem[]
  checkoutId: string
  metafields: CheckoutMetafield[]
  note: string
}

export default class NacelleGraphQLConnector extends NacelleConnector {
  endpoint: string
  spaceId: string
  token: string

  constructor(params: NacelleGraphQLConnectorParams) {
    super(params)

    if (
      typeof params.endpoint === 'undefined' ||
      params.endpoint === null ||
      params.endpoint === ''
    ) {
      throw new Error('NacelleGraphQLConnector requires a valid endpoint.')
    }

    if (
      typeof params.spaceId === 'undefined' ||
      params.spaceId === null ||
      params.spaceId === ''
    ) {
      throw new Error(
        'NacelleGraphQLConnector requires a valid Nacelle space id.'
      )
    }

    if (
      typeof params.token === 'undefined' ||
      params.token === null ||
      params.token === ''
    ) {
      throw new Error(
        'NacelleGraphQLConnector requires a valid Nacelle space API token.'
      )
    }

    this.endpoint = params.endpoint
    this.spaceId = params.spaceId
    this.token = params.token

    this.apiClient =
      params && params.httpClient
        ? params.httpClient
        : new HttpClientAxios({
            headers: {
              'Content-Type': 'application/json',
              'x-nacelle-space-id': this.spaceId,
              'x-nacelle-space-token': this.token
            }
          })
  }

  async request<T>(
    query: string,
    queryName: string,
    variables?: any
  ): Promise<NacelleGraphQLRequestResponse<T>> {
    try {
      const data = {
        query,
        variables
      }
      const response = await this.apiClient.post<GraphQLResponseRoot>(
        this.endpoint,
        data
      )

      if (response) {
        if (
          response.data &&
          Array.isArray(response.data.errors) &&
          response.data.errors.length > 0
        ) {
          const firstError = response.data.errors.pop()

          if (firstError && firstError.message) {
            throw new Error(firstError.message)
          }

          throw new Error(`Unknown graphql error attempting ${queryName}`)
        }

        if (
          response.data.data &&
          typeof response.data.data[queryName] !== 'undefined'
        ) {
          return {
            success: true,
            data: response.data.data[queryName]
          }
        } else {
          throw new Error(
            `Response did not include ${queryName}.
            ${JSON.stringify(response.data, null, 2)}`
          )
        }
      }

      throw new Error(`No valid response from ${queryName}`)
    } catch (error) {
      return {
        success: false,
        error: error.message,
        data: null
      }
    }
  }

  async getAllPageItems<T>(params: {
    query: string
    queryName: string
    first?: number
  }): Promise<Array<T>> {
    let after = ''
    const pages = []

    do {
      const response = await this.request<GraphQLPageResponse<T>>(
        params.query,
        params.queryName,
        {
          first: params.first || 500,
          after
        }
      )

      if (response.success && response.data) {
        const page = response.data

        pages.push(page)
        after = page.nextToken
      } else {
        after = ''
      }
    } while (after !== '')

    return pages.reduce((arr, page) => {
      if (page && page.items && page.items.length > 0) {
        return arr.concat(page.items)
      }

      return arr
    }, [] as Array<T>)
  }

  async allProducts(params?: { limit?: number }): Promise<Product[]> {
    const query = getProductsQuery
    const products = await this.getAllPageItems<ProductOptions>({
      query,
      queryName: 'getProducts',
      first: params && params.limit ? params.limit : 2000
    })

    return products.map(product => createProduct(product))
  }

  async allCollections(params?: { limit?: number }): Promise<Collection[]> {
    const query = getCollectionsQuery
    const collections = await this.getAllPageItems<CreateCollectionOptions>({
      query,
      queryName: 'getCollections',
      first: params && params.limit ? params.limit : 2000
    })

    return collections.map(collection => createCollection(collection))
  }

  async allContent(params?: { limit?: number }): Promise<Content[]> {
    const query = getContentQuery
    const content = await this.getAllPageItems<CreateContentOptions>({
      query,
      queryName: 'getContent',
      first: params && params.limit ? params.limit : 2000
    })

    return content.map(entry => createContent(entry))
  }

  async space(): Promise<Space> {
    const query = getSpaceQuery
    const response = await this.request<CreateSpaceOptions>(query, 'getSpace')

    if (response) {
      if (response.success && response.data) {
        return createSpace(response.data)
      }

      if (response && !response.success && response.error) {
        throw new Error(response.error)
      }
    }

    throw new Error('Invalid response attempting getSpace query')
  }

  async processCheckout(params: ProcessCheckoutParams): Promise<Checkout> {
    const query = processCheckoutMutation
    const response = await this.request<Checkout>(query, 'processCheckout', {
      input: {
        ...params
      }
    })

    if (response) {
      if (response.success && response.data) {
        return response.data
      }

      if (response && !response.success && response.error) {
        throw new Error(response.error)
      }
    }

    throw new Error('Invalid response attempting processCheckout mutation')
  }

  async queryCheckout(params: { id: string; url: string }): Promise<Checkout> {
    const query = getCheckoutQuery
    const response = await this.request<Checkout>(query, 'getCheckout', {
      ...params
    })

    if (response) {
      if (response.success && response.data) {
        return response.data
      }

      if (response && !response.success && response.error) {
        throw new Error(response.error)
      }
    }

    throw new Error('Invalid response attempting getCheckout query')
  }

  async isVariantAvailable(params: {
    productId: string
    variantId: string
  }): Promise<boolean> {
    const query = getVariantAvailabilityQuery
    const response = await this.request<boolean>(
      query,
      'getVariantAvailability',
      {
        ...params
      }
    )

    if (response) {
      if (
        response.success &&
        typeof response.data !== 'undefined' &&
        response.data !== null
      ) {
        return response.data
      }

      if (response && !response.success && response.error) {
        throw new Error(response.error)
      }
    }

    throw new Error('Invalid response attempting getVariantAvailability query')
  }
}
